import {
  GetTrialSubscriptionListQueryVariables,
  Omit,
  PageInformation,
  TrialSubscriptionReportDataConnectionFragment,
  TrialSubscriptionReportDataFragment,
  TrialUsageStatsDataFragment,
  TrialUserInfoDataFragment,
  useGetTrialSubscriptionListQuery
} from "generated/graphql";
import { parseDateStringToMilliseconds } from "utilities/datetime";
import { useMemo } from "react";
import * as Urql from "polygraphql";

export type TrialUser = {
  cursor: any;
  id: string;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  email: string | null;
  creationTime: number;
  browserType: string | null;
  meetingId: string;
  expired: boolean;
  numberOfCalls: number;
  totalDurationMinutes: number;
  totalNumberOfParticipants: number;
  minNumberOfParticipants: number;
  maxNumberOfParticipants: number;
  averageNumberOfParticipants: number;
};

export type TrialUserEdge = {
  cursor: string;
  node: TrialUser;
};

export type TrialUserConnection = {
  edges: Array<TrialUserEdge>;
  pageInfo: Pick<PageInformation, "hasNextPage" | "endCursor">;
  totalCount: number;
};

export const toTrialUser = (
  data: TrialSubscriptionReportDataFragment,
  cursor: string
): TrialUser => {
  const userData: Omit<TrialUserInfoDataFragment, "__typename"> = data.user ?? {
    email: null,
    familyName: null,
    givenName: null,
    name: null
  };
  const usageStats: Omit<
    TrialUsageStatsDataFragment,
    "__typename"
  > = data.usageStats ?? {
    averageNumberOfParticipants: 0,
    conferenceReports: [],
    conferencesUsageCounter: 0,
    maxNumberOfParticipants: 0,
    minNumberOfParticipants: 0,
    totalDurationMinutes: 0,
    totalNumberOfParticipants: 0
  };
  return {
    cursor: cursor,
    id: data.id,
    firstName: userData.givenName,
    lastName: userData.familyName,
    company: null,
    email: userData.email,
    creationTime: parseDateStringToMilliseconds(data.subscriptionDate),
    browserType: data.browserType,
    meetingId: data.meetingRoomId,
    expired: data.expired,
    numberOfCalls: usageStats.conferencesUsageCounter ?? 0,
    totalDurationMinutes: usageStats.totalDurationMinutes ?? 0,
    totalNumberOfParticipants: usageStats.totalNumberOfParticipants ?? 0,
    minNumberOfParticipants: usageStats.minNumberOfParticipants ?? 0,
    maxNumberOfParticipants: usageStats.maxNumberOfParticipants ?? 0,
    averageNumberOfParticipants: usageStats.averageNumberOfParticipants ?? 0
  };
};

export const toTrialUserConnection = (
  connection: TrialSubscriptionReportDataConnectionFragment
): TrialUserConnection => {
  let edges: Array<TrialUserEdge> = [];

  if (connection && connection.edges) {
    edges = connection.edges?.map(edge => {
      return {
        node: toTrialUser(edge.node, edge.cursor),
        cursor: edge.cursor
      };
    });
  }

  return {
    totalCount: connection.total,
    pageInfo: connection.pageInfo,
    edges: edges
  };
};

export type GetTrialUserListListOptions = Omit<
  Urql.UseQueryArgs<GetTrialSubscriptionListQueryVariables>,
  "query"
>;

export const useGetTrialUserList = (
  options: GetTrialUserListListOptions = {}
) => {
  const queryResults = useGetTrialSubscriptionListQuery({
    ...options
  });

  const data = useMemo(() => {
    let result;
    const sourceData = queryResults.data?.getTrialSubscriptions;
    if (sourceData) {
      result = toTrialUserConnection(sourceData);
    }
    return result;
  }, [queryResults.data]);

  return { ...queryResults, data };
};
